/**
 * Defensive JSON parser for transcript blobs served by the API.
 *
 * `Transcript` / `TranscriptSegment` in `./types/types.ts` are the single client
 * declaration of the wire shape, mirroring the Rust structs in
 * `crates/recording-types/src/lib.rs`. This file is the parser, not a second
 * declaration of that shape: `TranscriptData` / `TranscriptSegmentData` are
 * aliases kept so the existing render call sites keep compiling.
 */

import {
  isTranscriptSegment,
  TRANSCRIPT_SCHEMA_VERSION,
  type Transcript,
  type TranscriptSegment,
} from './types/types';

export type TranscriptSegmentData = TranscriptSegment;
export type TranscriptData = Transcript;

/**
 * Parse a raw transcript blob (from Tauri invoke or API response) into a
 * validated `TranscriptData` or `null`.
 *
 * Returns `null` when:
 * - Input is null, undefined, or not an object
 * - `segments` is missing or not an array
 * - `schema_version` is present and is not `TRANSCRIPT_SCHEMA_VERSION`
 *
 * Individual segments that are missing required fields (`start_ms`, `end_ms`,
 * `text`) are silently skipped. `speaker` is optional.
 *
 * `language` and `provider` fall back to `"unknown"`. A missing
 * `schema_version` is read as the supported version — it was absent from the
 * earliest blobs — but a present, unsupported one is rejected rather than
 * defaulted, and logged: the null return is indistinguishable from "no
 * transcript" once it reaches the UI.
 */
export function parseTranscript(raw: unknown): TranscriptData | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const obj = raw as Record<string, unknown>;

  if (!Array.isArray(obj['segments'])) {
    return null;
  }

  const schemaVersion =
    typeof obj['schema_version'] === 'number'
      ? obj['schema_version']
      : TRANSCRIPT_SCHEMA_VERSION;
  if (schemaVersion !== TRANSCRIPT_SCHEMA_VERSION) {
    // Returning null here is indistinguishable, in the UI, from a recording
    // that has no transcript at all. Log it so a server-side schema bump that
    // outpaces this client is observable rather than silently empty.
    console.error(
      `parseTranscript: unsupported schema_version ${schemaVersion} ` +
        `(this client supports ${TRANSCRIPT_SCHEMA_VERSION}); transcript not rendered`,
    );
    return null;
  }

  const segments: TranscriptSegmentData[] = (obj['segments'] as unknown[]).filter(
    isTranscriptSegment,
  );

  return {
    schema_version: schemaVersion,
    language: typeof obj['language'] === 'string' ? obj['language'] : 'unknown',
    provider: typeof obj['provider'] === 'string' ? obj['provider'] : 'unknown',
    segments,
  };
}
